<template>
	<v-container>
		<v-form ref="form" v-model="valid" @submit.prevent="true">
			<v-row class="contentRow">
				<!--'Question Number' icon-->
				<v-col cols="12">
					<h1 v-if="preview">Preview Onsite-Audit Template '{{ name }}'</h1>
					<h2 v-else class="font-weight-medium text-center">AUDIT REQUEST '{{ name }}'</h2>
					<h1 class="font-weight-bold pt-4" v-if="submitterName">Sent to {{submitterName}}</h1>
				</v-col>
			</v-row>
			<div v-for="(question, index) in questions" v-bind:key="index">
				<div v-if="index == 0" />
				<v-row no-gutters class="my-12" v-else-if="question.type == 'spacer' && shouldDisplay(index)">
				</v-row>
				<v-row
					no-gutters
					v-else-if="question.type == 'title'"
				>
					<v-col cols="6" class="mr-auto white--text">
						<h2>{{ question.name }}</h2>
					</v-col>
					<!-- Details/hint expanding field -->
					<v-col cols="12">
						<v-btn
							class="my-3"
							dark
							text
							v-if="!!question.hint"
							@click.stop="toggleHint(index)"
						>
							{{ !!question.hintVisible ? "Hide" : "Show" }}
							Question Details
							<v-icon right>far fa-question-circle</v-icon>
						</v-btn>
					</v-col>

					<v-col cols="12" v-if="!!question.hintVisible">
						<v-card outlined class="my-3 white--text">
							<v-card-text style="white-space: pre-wrap">{{
								question.hint
							}}</v-card-text>
						</v-card>
					</v-col>
					<v-col cols="12">
						<p class="float-right white--text text-caption">
							<i>(Question ID {{ index }})</i>
						</p>
					</v-col>
				</v-row>
				<v-row
					no-gutters
					align="center"
					v-else-if="question.type == 'attachment' && shouldDisplay(index)"
					dark
				>
					<v-col cols="6" class="mr-auto white--text">
						<h2>
							<v-icon dark left>fas fa-file-download</v-icon
							>{{ question.name }}
						</h2>
					</v-col>
					<!--'File upload' text field-->
					<v-col cols="12" class="white--text">
						<p class="mt-2">
							<i>The Auditor has provided file(s) for you to download:</i>
						</p>
						<file-uploader
							:value="question.fileID"
							:readonly="true"
							:disabled="true"
							:dark="true"
							:name="question.name"
						/>
					</v-col>
					<!-- Details/hint expanding field -->
					<v-col cols="12">
						<v-btn
							class="my-3"
							dark
							text
							v-if="!!question.hint"
							@click.stop="toggleHint(index)"
						>
							{{ !!question.hintVisible ? "Hide" : "Show" }}
							Question Details
							<v-icon right>far fa-question-circle</v-icon>
						</v-btn>
					</v-col>
					<v-col cols="12" v-if="!!question.hintVisible">
						<v-card outlined class="my-3 white--text">
							<v-card-text style="white-space: pre-wrap">{{
								question.hint
							}}</v-card-text>
						</v-card>
					</v-col>
					<v-col cols="12">
						<p class="float-right grey--text text-caption">
							<i>(Question ID {{ index }})</i>
						</p>
					</v-col>
				</v-row>
				<v-row no-gutters v-else-if="shouldDisplay(index)">
					<!--'Question Number' icon-->
					<!-- <v-col cols="1" v-if="question.type != 'title'">
					<v-avatar dense tile color="secondary">
						<h3 class="white--text">#{{index}}</h3>
					</v-avatar>
					</v-col>-->
					<!--'Title Name' label-->
					<!--'Question Name' label-->
					<v-col cols="12">
						<h3 class="my-3">
							{{ question.name
							}}<sup v-if="!!question.required"
								><v-icon color="error" x-small right
									>fas fa-asterisk</v-icon
								></sup
							>
						</h3>
					</v-col>
					<!--'Question Name' text field-->
					<v-col cols="12" v-if="question.type == 'singleanswer'">
						<v-text-field
							dense
							align="center"
							label="Answer"
							hide-details="auto"
							placeholder="Enter your answer:"
							:rules="question.required ? notEmptyRules : []"
							outlined
							v-model="answers[index]"
							class="my-3"
						></v-text-field>
					</v-col>
					<!--'Dependency Question' text field-->
					<v-col cols="12" v-if="question.type == 'multiplechoice'">
						<v-select
							dense
							outlined
							v-model="answers[index]"
							:items="question.possibilities"
							menu-props="auto"
							label="Please Choose"
							hide-details="auto"
							single-line
							:rules="question.required ? notEmptyRules : []"
							class="my-3 text-wrap"
						>
							<template v-slot:item="{ item }">
								<span class="py-2">
									<v-avatar color="primary" size="5" class="mr-3"></v-avatar>
									{{ item }}</span
								>
							</template>
						</v-select>
					</v-col>
					<!--'Dependency Question' text field-->
					<v-col cols="12" v-if="question.type == 'multipleanswers'">
						<v-select
							dense
							outlined
							multiple
							v-model="answers[index]"
							:items="question.possibilities"
							menu-props="auto"
							label="Please Choose"
							hide-details="auto"
							single-line
							:rules="question.required ? notEmptyRules : []"
							class="my-3 text-wrap"
						>
							<template v-slot:item="{ active, item, attrs, on }">
								<v-list-item v-on="on" v-bind="attrs" #default="{ active }">
									<v-checkbox
										color="primary"
										size="5"
										class="mr-3"
										:input-value="active"
									/>{{ item }}
								</v-list-item>
							</template>
						</v-select>
					</v-col>
					<!--'File upload' text field-->
					<v-col cols="12" v-if="question.type == 'file'">
						<file-uploader
							v-model="answers[index]"
							:rules="question.required ? notEmptyRules : []"
							:disabled="true"
							@filesChanged="saveAudit()"
						/>
						<p v-if="!!preview">
							<i>File uploads are disabled in preview mode</i>
						</p>
					</v-col>
					<!-- Details/hint expanding field -->
					<v-col cols="12">
						<v-btn
							class="my-3"
							text
							v-if="!!question.hint"
							@click.stop="toggleHint(index)"
						>
							{{ !!question.hintVisible ? "Hide" : "Show" }}
							Question Details
							<v-icon right>far fa-question-circle</v-icon>
						</v-btn>
					</v-col>

					<v-col cols="12" v-if="!!question.hintVisible">
						<v-card outlined class="my-3">
							<v-card-text style="white-space: pre-wrap">{{
								question.hint
							}}</v-card-text>
						</v-card>
					</v-col>
					<v-col cols="12">
						<p class="float-right grey--text text-caption">
							<i>(Question ID {{ index }})</i>
						</p>
					</v-col>
				</v-row>
			</div>
			<v-row
				no-gutters
				class="contentRow"
				style="border: 2px solid #4CAF50; /*#3d2645*/"
			>
				<v-col cols="12" class="mr-auto">
					<h2>Ready to send?</h2>
					<p>
						Note: You will not be able to change your answers at a later time.
					</p>
					<p>
						<i>Submitting is disabled in preview mode.</i>
					</p>
					<v-btn
						color="success"
						:disabled="true"
						type="submit"
						align="center"
						class="mt-4"
					>
						Submit Audit
						<v-icon right>fas fa-paper-plane</v-icon>
					</v-btn>
				</v-col>
			</v-row>
		</v-form>
	</v-container>
</template>

<script>
import TemplateAPI from "../../api/audit/template"
import LoadingState from "../../consts/LoadingState"
import FilesAPI from "../../api/files/files"
import FileUploaderVue from "../../components/FileUploader.vue"
export default {
	name: "OnsiteAuditRequestView",
	components: {
		"file-uploader": FileUploaderVue
	},
	data() {
		return {
			sheet: false,
			notEmptyRules: [v => !!v || "This field cannot be empty"],
			valid: false,
			questions: null,
			answers: [],
			files: [],
			name: "Audit",
			id: null,
			fileID: null
		}
	},
	props: {
		preview: Boolean,
		previewID: Number,
		submitterName: String
	},
	mounted() {
		this.loadPreview()
	},
	computed: {
		unsaved() {
			return false
		}
	},
	methods: {
		loadPreview() {
			this.$store.dispatch(
				"loading/" + LoadingState.ACTION_START_LOADING,
				null,
				{ root: true }
			)
			TemplateAPI.getTemplate(this.previewID)
				.then(result => {
					this.name = result.name
					this.id = result.id
					this.questions = result.questions
					this.fileID = result.fileID

					this.answers = []
					this.files = []
					for (let i = 0; i < this.questions.length; i++) {
						this.answers.push(null)
						this.files.push(null)
					}
					this.$store.dispatch(
						"loading/" + LoadingState.ACTION_END_LOADING,
						null,
						{ root: true }
					)
				})
				.catch(error => {
					this.$store.dispatch(
						"loading/" + LoadingState.ACTION_SET_ERROR,
						error,
						{ root: true }
					)
				})
		},
		toggleHint(index) {
			this.$set(
				this.questions[index],
				"hintVisible",
				!this.questions[index].hintVisible
			)
		},
		fileChanged(index) {
			FilesAPI.createFile(this.files[index].name, this.files[index])
				.then(file => {
					this.$set(this.answers, index, file.id)
				})
				.catch(err => {
					this.files[index] = null
					console.error(err)
				})
		},
		removeFile(index) {
			FilesAPI.deleteFile(this.answers[index])
				.then(() => {
					this.$set(this.answers, index, null)
				})
				.catch(err => {
					this.$store.dispatch(
						"loading/" + LoadingState.ACTION_SET_ERROR,
						err,
						{ root: true }
					)
				})
		},
		downloadFile(fileID, name) {
			FilesAPI.download("" + fileID, name)
		},
		shouldDisplay(index) {
			if (!this.questions[index].dependencyQuestionID) {
				return true
			}
			if (!this.answers[this.questions[index].dependencyQuestionID]) {
				return false
			}
			if (
				this.questions[index].dependencyQuestionValue.includes(
					this.answers[this.questions[index].dependencyQuestionID]
				)
			) {
				return true
			}

			if (
				typeof this.answers[this.questions[index].dependencyQuestionID] ==
				"object"
			) {
				for (let element of this.answers[
					this.questions[index].dependencyQuestionID
				]) {
					if (this.questions[index].dependencyQuestionValue.includes(element)) {
						return true
					}
				}
			}
			return false
		}
	}
}
</script>